import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import LanguageSelector from "../components/LanguageSelector";
import { api } from "../lib/api";
import {
    ArrowRight, Sparkles, Users, GraduationCap, CheckCircle2,
    BookOpen, Mic, Layers, Brain, PenLine,
    Briefcase, FolderKanban, MessageSquareHeart, ClipboardList, Mail,
    Clock, ShieldCheck, Heart, Zap,
    Target, Repeat, Wallet, ChefHat, CalendarDays, ListChecks,
    Quote, ChevronDown,
} from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_project-sync-pro-1/artifacts/y56otaul_Virtually%20%281%29.png";

const STUDENT_FEATURES = [
    {
        icon: BookOpen,
        title: "Study Hub",
        copy: "All your modules, classes and assessments in one beautifully calm command centre.",
    },
    {
        icon: Mic,
        title: "Lecture Notes",
        copy: "Record live or upload audio. We transcribe with Whisper and turn it into smart, structured notes.",
    },
    {
        icon: Layers,
        title: "Missed Lecture Mode",
        copy: "Drop in the slides or course pack — we rebuild the lecture, surface knowledge gaps and give you a study schedule.",
    },
    {
        icon: Brain,
        title: "Revision Tools",
        copy: "Auto-generated flashcards, MCQs, practice exam questions and an AI tutor that only quizzes you on your materials.",
    },
    {
        icon: PenLine,
        title: "Academic Writing Tools",
        copy: "Reference checker, citation generator, grammar polish and ‘improve flow’ with version history.",
    },
];

const PRO_FEATURES = [
    {
        icon: Briefcase,
        title: "Client Management",
        copy: "Contacts, contracts, payments and documents — everything you need to keep clients happy in one place.",
    },
    {
        icon: FolderKanban,
        title: "Project Management",
        copy: "Milestones, tasks and subtasks with progress that calculates itself from real completed work.",
    },
    {
        icon: MessageSquareHeart,
        title: "Client Memory",
        copy: "Every concern, preference and decision remembered. Walk into the next call already up to speed.",
    },
    {
        icon: ClipboardList,
        title: "AI Meeting Notes",
        copy: "Record or upload meetings. We summarise, pull out action items and assign decisions automatically.",
    },
    {
        icon: Mail,
        title: "Follow-Up Generation",
        copy: "Drafted follow-up emails in your tone, with the right action items, ready to send in one click.",
    },
];

const STUDENT_FLOW = [
    "9:02 — Miss the lecture. Upload the slides into Missed Lecture Mode.",
    "9:05 — AI rebuilds the lecture: summary, key concepts, knowledge gaps.",
    "9:10 — Flashcards and a 12-question quiz appear, tailored to those slides.",
    "9:25 — Quiz score logged, weak topics highlighted, revision plan ready.",
    "Tomorrow — AI tutor revises only the gaps. Walk into class confident.",
];

const PRO_FLOW = [
    "Mon 10:00 — Client call recorded. Upload to AI Meeting Hub.",
    "Mon 10:02 — Smart notes generated: decisions, risks, action items.",
    "Mon 10:03 — Five tasks created, two concerns logged in Client Memory.",
    "Mon 10:04 — Follow-up email drafted in your voice, ready to send.",
    "Friday — Open the client. The whole story is right there waiting.",
];

const BENEFITS = [
    { icon: Clock, title: "Hours back, weekly", copy: "Stop typing meeting notes and chasing your own to-do list." },
    { icon: ShieldCheck, title: "Nothing falls through", copy: "Action items, follow-ups and revision gaps are tracked for you." },
    { icon: Heart, title: "Built for warmth", copy: "Not another cold productivity dashboard — a workspace that feels human." },
    { icon: Zap, title: "Ready in minutes", copy: "Sign up, pick your mode, and start with a 7-day free trial. No card needed." },
];

export default function Landing() {
    // Optional CMS-backed override for Testimonials + FAQ. Falls back to
    // the module-level defaults if the fetch fails or content is empty —
    // marketing page must render even if the API is unreachable.
    const [override, setOverride] = useState({ testimonials: null, faqs: null });
    useEffect(() => {
        api.get("/landing/content")
            .then((r) => setOverride({
                testimonials: r.data?.testimonials?.length ? r.data.testimonials : null,
                faqs: r.data?.faqs?.length ? r.data.faqs : null,
            }))
            .catch(() => {});
    }, []);
    return (
        <div className="min-h-screen grain relative" data-testid="landing-page">
            <Header />
            <Hero />
            <ValueStrip />
            <WhoFor />
            <FeatureSection role="student" title="If you're a student" subtitle="study hub"
                            tagline="Every lecture, recorded, rebuilt and revised."
                            features={STUDENT_FEATURES} flow={STUDENT_FLOW}
                            flowTitle="A morning with Virtually Unstoppable" />
            <FeatureSection role="professional" title="If you're a professional" subtitle="client hub"
                            tagline="Win the next meeting before it even starts."
                            features={PRO_FEATURES} flow={PRO_FLOW}
                            flowTitle="A Monday with Virtually Unstoppable" />
            <Benefits />
            <PersonalSuite />
            <Pricing />
            <Testimonials items={override.testimonials} />
            <FAQ items={override.faqs} />
            <FinalCTA />
            <FooterMini />
        </div>
    );
}

function Header() {
    return (
        <header className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 pt-6 flex items-center justify-between gap-3 relative z-[2]" data-testid="landing-header">
            <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="landing-brand">
                <img src={LOGO} alt="Virtually Unstoppable" className="h-10 w-10 sm:h-12 sm:w-12 object-contain shrink-0" />
                <div className="leading-tight hidden xs:block sm:block">
                    <div className="font-serif font-semibold text-base sm:text-lg">Virtually</div>
                    <div className="font-script text-[var(--brand-orange)] -mt-1 text-lg sm:text-xl">Unstoppable</div>
                </div>
            </Link>
            <nav className="hidden md:flex items-center gap-6 text-sm text-[var(--text-ink)]/75">
                <a href="#student" className="hover:text-[var(--brand-orange)] transition" data-testid="nav-student">Students</a>
                <a href="#professional" className="hover:text-[var(--brand-purple)] transition" data-testid="nav-pro">Professionals</a>
                <a href="#pricing" className="hover:text-[var(--brand-magenta)] transition" data-testid="nav-pricing">Pricing</a>
            </nav>
            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                <LanguageSelector compact testid="landing-language-selector" />
                <Link to="/login" data-testid="nav-login" className="btn-ghost text-xs sm:text-sm px-2 sm:px-3">
                    <span className="hidden sm:inline">Sign in</span>
                    <span className="sm:hidden">Login</span>
                </Link>
                <Link to="/signup" data-testid="nav-signup" className="btn-primary text-xs sm:text-sm px-2.5 sm:px-3">
                    <span className="hidden sm:inline">Get started</span>
                    <span className="sm:hidden">Start</span>
                </Link>
            </div>
        </header>
    );
}

function Hero() {
    return (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 pt-12 md:pt-20 pb-12 relative z-[2]" data-testid="hero-section">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-7 fade-up">
                    <div className="font-script text-[var(--brand-orange)] text-2xl md:text-3xl mb-2">
                        rise above the ordinary
                    </div>
                    <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light leading-[1.05] tracking-tight break-words" data-testid="hero-headline">
                        Built for people
                        <br />
                        <span className="brand-gradient-text italic">always juggling too much.</span>
                    </h1>
                    <p className="mt-6 text-base sm:text-lg md:text-xl text-[var(--text-ink)]/75 max-w-2xl" data-testid="hero-subtitle">
                        One intelligent workspace designed to help you organise your work, studies and life — powered by AI and built to keep everything in one place.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <Link to="/signup" data-testid="hero-cta-start" className="btn-primary inline-flex items-center gap-2 text-base px-5 py-3">
                            Start 7-day free trial <ArrowRight size={16} />
                        </Link>
                        <Link to="/login" className="btn-ghost">I already have an account</Link>
                    </div>
                    <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[var(--text-ink)]/70">
                        <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[var(--status-green)]"/> No card required</span>
                        <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[var(--status-green)]"/> Cancel anytime</span>
                        <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[var(--status-green)]"/> UK-built · GDPR-aligned</span>
                    </div>
                </div>

                <div className="lg:col-span-5 fade-up delay-2">
                    <div className="card-warm p-6 relative overflow-hidden">
                        <img
                            src="https://images.pexels.com/photos/673018/pexels-photo-673018.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                            alt=""
                            className="w-full h-64 object-cover rounded-2xl"
                        />
                        <div className="mt-5 grid grid-cols-2 gap-3">
                            <div className="card-warm p-4">
                                <GraduationCap size={18} className="text-[var(--brand-orange)] mb-2" />
                                <div className="font-serif text-xl">Student</div>
                                <div className="text-sm text-[var(--text-ink)]/65">Lectures, revision, references, writing</div>
                            </div>
                            <div className="card-warm p-4">
                                <Users size={18} className="text-[var(--brand-purple)] mb-2" />
                                <div className="font-serif text-xl">Professional</div>
                                <div className="text-sm text-[var(--text-ink)]/65">Clients, meetings, follow-ups, projects</div>
                            </div>
                        </div>
                        <div className="mt-4 flex items-center gap-2 text-sm">
                            <Sparkles size={14} className="text-[var(--brand-magenta)]" />
                            <span className="font-script text-xl text-[var(--brand-magenta)]">More account types coming soon</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function ValueStrip() {
    return (
        <section className="bg-[rgba(123,45,142,0.05)] border-y border-[rgba(26,19,24,0.06)]" data-testid="value-strip">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-6 grid grid-cols-1 sm:grid-cols-3 gap-6 text-center sm:text-left">
                <Pillar number="01" headline="Capture" body="Lectures, meetings, voice notes — recorded or uploaded." />
                <Pillar number="02" headline="Understand" body="AI turns them into structured notes, action items and quizzes." />
                <Pillar number="03" headline="Finish" body="Tasks, follow-ups and revision plans appear ready to go." />
            </div>
        </section>
    );
}

function Pillar({ number, headline, body }) {
    return (
        <div className="flex items-start gap-3">
            <div className="font-script text-[var(--brand-orange)] text-3xl leading-none">{number}</div>
            <div>
                <div className="font-serif text-xl">{headline}</div>
                <div className="text-sm text-[var(--text-ink)]/70">{body}</div>
            </div>
        </div>
    );
}

function WhoFor() {
    return (
        <section id="who-for" className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-20 relative z-[2]" data-testid="who-for-section">
            <div className="font-script text-[var(--brand-magenta)] text-2xl">choose your mode</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-1 max-w-3xl" data-testid="who-for-heading">
                Pick the experience that <span className="italic">fits you today.</span>
            </h2>
            <p className="mt-4 text-lg text-[var(--text-ink)]/70 max-w-3xl" data-testid="who-for-subtitle">
                Students and professionals each get a workspace tuned to the way they actually work — and the platform grows with you as your needs change.
            </p>

            <div className={`grid gap-6 mt-10 ${PERSONAS.length === 1 ? "md:grid-cols-1" : PERSONAS.length === 2 ? "md:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-3"}`}>
                {PERSONAS.map((p) => (
                    <PersonaCard key={p.testid} {...p} />
                ))}
            </div>

            <p className="mt-8 text-sm text-[var(--text-ink)]/60 max-w-2xl" data-testid="who-for-future">
                Pick the experience that fits you today — more account types are on the way.
            </p>
        </section>
    );
}

const PERSONAS = [
    {
        accent: "orange",
        icon: GraduationCap,
        label: "Students",
        headline: "The course is fast. Your notes shouldn't slow you down.",
        bullets: [
            "Behind on a module? Drop the slides in.",
            "Missed a lecture? We rebuild it from the materials.",
            "Need to revise? Flashcards and quizzes are already there.",
            "Writing an essay? Reference Checker has your back.",
        ],
        cta: "See the student tools",
        ctaHref: "#student",
        testid: "persona-student",
    },
    {
        accent: "purple",
        icon: Briefcase,
        label: "Professionals",
        headline: "Win back the hours you lose to admin every week.",
        bullets: [
            "Every client conversation captured, summarised, indexed.",
            "Follow-ups drafted before you close your laptop.",
            "Projects with progress that reflects real work, not vibes.",
            "Walk into the next call already knowing the story.",
        ],
        cta: "See the professional tools",
        ctaHref: "#professional",
        testid: "persona-pro",
    },
    // Future account types (e.g. Personal) can be appended here without
    // touching layout — the grid above adapts from 1 → 2 → 3 columns.
];

function PersonaCard({ accent, icon: Icon, label, headline, bullets, cta, ctaHref, testid }) {
    const accentColor = accent === "orange" ? "var(--brand-orange)" : "var(--brand-purple)";
    return (
        <div className="card-warm p-7 relative overflow-hidden" data-testid={testid}>
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-10" style={{ background: accentColor }} />
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em]" style={{ color: accentColor }}>
                <Icon size={14} /> {label}
            </div>
            <h3 className="font-serif text-2xl md:text-3xl mt-2 leading-tight">{headline}</h3>
            <ul className="mt-4 space-y-2 text-[var(--text-ink)]/80">
                {bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-base">
                        <CheckCircle2 size={16} className="mt-1 shrink-0" style={{ color: accentColor }} />
                        <span>{b}</span>
                    </li>
                ))}
            </ul>
            <a href={ctaHref} className="inline-flex items-center gap-1 mt-6 text-sm font-medium hover:gap-2 transition-all" style={{ color: accentColor }}>
                {cta} <ArrowRight size={14} />
            </a>
        </div>
    );
}

function FeatureSection({ role, title, subtitle, tagline, features, flow, flowTitle }) {
    const accent = role === "student" ? "var(--brand-orange)" : "var(--brand-purple)";
    return (
        <section id={role} className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-20 relative z-[2]" data-testid={`features-${role}`}>
            <div className="font-script text-2xl" style={{ color: accent }}>{subtitle}</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-1">{title}</h2>
            <p className="mt-3 text-lg md:text-xl text-[var(--text-ink)]/75 max-w-3xl">{tagline}</p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
                {features.map((f, i) => (
                    <FeatureCard key={f.title} icon={f.icon} title={f.title} copy={f.copy} accent={accent}
                                 testid={`feature-${role}-${i}`} />
                ))}
                <WorkflowCard title={flowTitle} steps={flow} accent={accent} testid={`workflow-${role}`} />
            </div>
        </section>
    );
}

function FeatureCard({ icon: Icon, title, copy, accent, testid }) {
    return (
        <div className="card-warm p-5 transition hover:-translate-y-0.5 hover:shadow-md" data-testid={testid}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${accent}15`, color: accent }}>
                <Icon size={20} />
            </div>
            <div className="font-serif text-xl">{title}</div>
            <div className="text-sm text-[var(--text-ink)]/70 mt-2 leading-relaxed">{copy}</div>
        </div>
    );
}

function WorkflowCard({ title, steps, accent, testid }) {
    return (
        <div className="card-warm p-5 md:col-span-2 lg:col-span-3 mt-2" data-testid={testid}>
            <div className="flex items-baseline gap-2 mb-3">
                <Sparkles size={16} style={{ color: accent }} />
                <div className="font-serif text-xl">{title}</div>
            </div>
            <ol className="grid md:grid-cols-5 gap-3 list-none">
                {steps.map((s, i) => (
                    <li key={i} className="flex gap-2 text-sm text-[var(--text-ink)]/80" data-testid={`${testid}-step-${i}`}>
                        <span className="font-script text-2xl leading-none shrink-0" style={{ color: accent }}>{String(i + 1).padStart(2, "0")}</span>
                        <span className="leading-snug">{s}</span>
                    </li>
                ))}
            </ol>
        </div>
    );
}

function Benefits() {
    return (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-20 relative z-[2]" data-testid="benefits-section">
            <div className="grid md:grid-cols-4 gap-4">
                {BENEFITS.map((b, i) => (
                    <div key={b.title} className="card-warm p-5" data-testid={`benefit-${i}`}>
                        <b.icon size={22} className="text-[var(--brand-magenta)] mb-2" />
                        <div className="font-serif text-xl">{b.title}</div>
                        <div className="text-sm text-[var(--text-ink)]/70 mt-1">{b.copy}</div>
                    </div>
                ))}
            </div>
        </section>
    );
}

const PERSONAL_TILES = [
    { icon: Target, title: "Goals mood-board", copy: "Pin the dreams that pull you forward. Visual goal-setting that actually sticks." },
    { icon: Repeat, title: "Habit tracker", copy: "Daily checks, streaks and a flame that turns up after three days." },
    { icon: Wallet, title: "Finance & budgets", copy: "Income, expenses, budgets — plus a UK student-loan + tax calculator." },
    { icon: ChefHat, title: "Meal planner & recipes", copy: "Save favourite recipes and plan the week so you actually eat." },
    { icon: CalendarDays, title: "Calendar & deadlines", copy: "Month, week and agenda views. Assessments and deadlines auto-merge in." },
    { icon: ListChecks, title: "Daily to-do", copy: "A calm, colour-coded four-column board for today, only today." },
];

function PersonalSuite() {
    return (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 pb-20 relative z-[2]" data-testid="personal-suite">
            <div className="font-script text-[var(--brand-orange)] text-2xl">and the rest of your life</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-1 max-w-3xl">Everything else, in the same calm place.</h2>
            <p className="mt-3 text-lg text-[var(--text-ink)]/70 max-w-3xl">
                Work isn&rsquo;t the whole picture. Virtually Unstoppable ships with the personal side baked in — so you don&rsquo;t need five more apps to keep yourself together.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
                {PERSONAL_TILES.map((t, i) => (
                    <div key={t.title} className="card-warm p-5 transition hover:-translate-y-0.5 hover:shadow-md" data-testid={`personal-tile-${i}`}>
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                             style={{ background: "rgba(123,45,142,0.12)", color: "var(--brand-purple)" }}>
                            <t.icon size={20} />
                        </div>
                        <div className="font-serif text-xl">{t.title}</div>
                        <div className="text-sm text-[var(--text-ink)]/70 mt-2 leading-relaxed">{t.copy}</div>
                    </div>
                ))}
            </div>
        </section>
    );
}

const PRICE_FEATURES_PRO = [
    "Unlimited clients & projects",
    "AI Meeting Hub (recorder + transcripts + smart notes)",
    "Client Memory & follow-up email drafting",
    "AI Contract Writer with PDF export",
    "Project breakdowns with auto-progress",
    "Document storage, calendar, daily to-do",
];
const PRICE_FEATURES_STU = [
    "Unlimited classes & assessments",
    "Study Hub: live recording + AI transcripts",
    "Missed Lecture Mode with revision toolkit",
    "AI Tutor restricted to your materials",
    "Reference Checker (Harvard, APA, MLA, Chicago)",
    "Improve Flow, grammar & spelling tools",
];

function Pricing() {
    return (
        <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-20 relative z-[2]" data-testid="pricing-section">
            <div className="font-script text-[var(--brand-magenta)] text-2xl">simple, honest pricing</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-1 max-w-3xl">One plan per persona. <span className="italic">No hidden tiers.</span></h2>
            <p className="mt-3 text-lg text-[var(--text-ink)]/70 max-w-3xl">
                Seven days free, no card required. Cancel any time. Prices in pounds sterling, VAT inclusive where applicable.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mt-10">
                <PriceCard
                    accent="orange"
                    label="Student"
                    price="£5"
                    cadence="per month"
                    headline="Everything you need to keep up — and get ahead."
                    features={PRICE_FEATURES_STU}
                    cta="Start as a student"
                    testid="pricing-card-student"
                />
                <PriceCard
                    accent="purple"
                    label="Professional"
                    price="£14"
                    cadence="per month"
                    headline="Run a calm, organised practice without the admin tax."
                    features={PRICE_FEATURES_PRO}
                    cta="Start as a professional"
                    testid="pricing-card-pro"
                    highlight
                />
            </div>

            <div className="mt-6 text-sm text-[var(--text-ink)]/60 flex flex-wrap gap-x-6 gap-y-2">
                <span className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[var(--status-green)]"/> 7-day free trial on both plans</span>
                <span className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[var(--status-green)]"/> No setup fees</span>
                <span className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[var(--status-green)]"/> Self-serve cancellation</span>
            </div>
        </section>
    );
}

function PriceCard({ accent, label, price, cadence, headline, features, cta, testid, highlight }) {
    const accentColor = accent === "orange" ? "var(--brand-orange)" : "var(--brand-purple)";
    return (
        <div className={`card-warm p-8 relative overflow-hidden ${highlight ? "ring-2 ring-[var(--brand-purple)]/30" : ""}`} data-testid={testid}>
            {highlight && (
                <div className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full brand-gradient text-white" data-testid={`${testid}-badge`}>
                    most popular
                </div>
            )}
            <div className="text-xs uppercase tracking-[0.18em]" style={{ color: accentColor }}>{label}</div>
            <div className="flex items-baseline gap-2 mt-2">
                <div className="font-serif text-5xl md:text-6xl" data-testid={`${testid}-price`} style={{ color: accentColor }}>{price}</div>
                <div className="text-sm text-[var(--text-ink)]/60">{cadence}</div>
            </div>
            <div className="font-serif text-xl mt-2">{headline}</div>
            <ul className="mt-5 space-y-2">
                {features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[var(--text-ink)]/80">
                        <CheckCircle2 size={16} className="mt-0.5 shrink-0" style={{ color: accentColor }} />
                        <span>{f}</span>
                    </li>
                ))}
            </ul>
            <Link to="/signup" data-testid={`${testid}-cta`} className="btn-primary inline-flex items-center gap-2 mt-6 w-full justify-center">
                {cta} <ArrowRight size={16} />
            </Link>
        </div>
    );
}

const TESTIMONIALS = [
    {
        quote:
            "Between client meetings and my dissertation, my head was noise. Virtually Unstoppable stitched the two lives together — I actually look forward to Mondays now.",
        name: "Priya S.",
        role: "MSc Product Design · Freelance UX Consultant",
        accent: "var(--brand-purple)",
    },
    {
        quote:
            "The AI meeting notes alone have paid for the whole subscription. I walk out of every client call with actions already in the tracker.",
        name: "Marcus L.",
        role: "Independent Strategy Consultant",
        accent: "var(--brand-magenta)",
    },
    {
        quote:
            "Missed Lecture Mode is genuinely magical. I upload the slides my classmate sent, and by dinner I have flashcards and a study plan ready.",
        name: "Ana R.",
        role: "Second-year Law Student",
        accent: "var(--brand-orange)",
    },
];

function Testimonials({ items }) {
    const rows = items && items.length ? items : TESTIMONIALS;
    return (
        <section
            id="testimonials"
            className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-16 lg:py-20 relative z-[2]"
            data-testid="testimonials-section"
        >
            <div className="text-center max-w-2xl mx-auto mb-10 lg:mb-14">
                <div className="font-script text-xl sm:text-2xl" style={{ color: "var(--brand-magenta)" }}>
                    what our users say
                </div>
                <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl mt-1" data-testid="testimonials-heading">
                    Built with, and for, real people.
                </h2>
                <p className="mt-3 text-[var(--text-ink)]/70 text-base sm:text-lg">
                    Early members of the phoenix club — students, consultants, and small-firm founders — on what changed for them.
                </p>
            </div>
            <div className="grid gap-5 md:gap-6 md:grid-cols-3">
                {rows.map((t, i) => (
                    <TestimonialCard key={i} {...t} testid={`testimonial-${i}`} />
                ))}
            </div>
        </section>
    );
}

function TestimonialCard({ quote, name, role, accent, testid }) {
    return (
        <figure
            className="card-warm p-6 sm:p-7 relative overflow-hidden flex flex-col h-full"
            data-testid={testid}
        >
            <div
                className="absolute -top-3 -right-3 w-14 h-14 rounded-full opacity-10"
                style={{ background: accent }}
                aria-hidden="true"
            />
            <Quote size={20} className="shrink-0" style={{ color: accent }} aria-hidden="true" />
            <blockquote className="mt-3 text-base sm:text-lg text-[var(--text-ink)]/85 leading-relaxed flex-1 break-words">
                “{quote}”
            </blockquote>
            <figcaption className="mt-5 pt-4 border-t border-[rgba(26,19,24,0.08)]">
                <div className="font-serif text-lg leading-tight" style={{ color: accent }}>
                    {name}
                </div>
                <div className="text-xs sm:text-sm text-[var(--text-ink)]/60 mt-0.5">{role}</div>
            </figcaption>
        </figure>
    );
}

const FAQS = [
    {
        q: "Is there really a free trial? What's the catch?",
        a: "Seven days, unrestricted, no credit card. You get every feature — AI meeting notes, missed-lecture mode, client memory, the lot. At the end you either pick a plan or your workspace stays available in read-only mode. Nothing is deleted; nothing auto-charges.",
    },
    {
        q: "How is my data kept secure?",
        a: "Every request is authenticated via short-lived JWTs; passwords are bcrypt-hashed; files live in Emergent Object Storage with per-user scoping so nobody — not even our support team — can browse your uploads. All traffic is TLS 1.2+. We never train external AI models on your data.",
    },
    {
        q: "Can I cancel any time?",
        a: "Any time, from Settings → Billing → Cancel. No phone calls, no forms, no dark patterns. You keep access until the end of the paid period, then automatically drop to the read-only tier. You can export your data before or after cancelling.",
    },
    {
        q: "What happens to my data if I cancel or delete my account?",
        a: "Cancel: everything stays available in read-only mode for as long as you like. Delete: your account, uploads, meeting notes, and AI history are permanently removed within 30 days. If you change your mind before then, contact support and we can restore it from the last daily snapshot.",
    },
    {
        q: "Do you use my meetings, notes, or documents to train AI?",
        a: "No. Your content is only ever sent to the AI provider (OpenAI / Anthropic / Google via our Universal Key) for the specific request you triggered — meeting summarisation, proofreading, etc. — and is never retained, indexed, or used for training on our side or theirs.",
    },
    {
        q: "Which plan should I pick — Student or Professional?",
        a: "Student is for anyone in higher education: Study Hub, Missed Lecture Mode, flashcards, AI proofreading. Professional is for freelancers and consultants: Client Memory, Meeting Intelligence, AI Contract Writer, project management. If your life spans both worlds, you can switch modes any time from Settings — everything you've created stays with you.",
    },
    {
        q: "Does it work on my phone? Is there an app?",
        a: "It's a full Progressive Web App, so you can install it straight from your phone's browser and get a proper home-screen icon, offline drafts, and push notifications. Native iOS and Android wrappers are on the roadmap. Right now the PWA covers every core flow — recording meetings, capturing tasks, checking off habits — on any modern phone.",
    },
    {
        q: "I have another question — how do I get in touch?",
        a: "Drop us a line at hello@virtuallyunstoppable.online. A real human replies within one business day, usually much faster. Bug reports, feature ideas, feedback on a rough day — all welcome.",
    },
];

function FAQ({ items }) {
    const rows = items && items.length ? items : FAQS;
    // First item open by default so the interaction is discoverable.
    const [openIdx, setOpenIdx] = useState(0);
    const toggle = (i) => setOpenIdx((cur) => (cur === i ? -1 : i));
    return (
        <section
            id="faq"
            className="max-w-4xl mx-auto px-4 sm:px-6 md:px-10 py-16 lg:py-20 relative z-[2]"
            data-testid="faq-section"
        >
            <div className="text-center mb-10 lg:mb-14">
                <div className="font-script text-xl sm:text-2xl" style={{ color: "var(--brand-purple)" }}>
                    common questions
                </div>
                <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl mt-1" data-testid="faq-heading">
                    Everything you might want to know.
                </h2>
                <p className="mt-3 text-[var(--text-ink)]/70 text-base sm:text-lg">
                    If your question isn&apos;t here, hello@virtuallyunstoppable.online reaches a real human within a day.
                </p>
            </div>
            <div className="space-y-3" data-testid="faq-list">
                {rows.map((item, i) => (
                    <FAQItem
                        key={i}
                        idx={i}
                        question={item.q}
                        answer={item.a}
                        open={openIdx === i}
                        onToggle={() => toggle(i)}
                    />
                ))}
            </div>
        </section>
    );
}

function FAQItem({ idx, question, answer, open, onToggle }) {
    const panelId = `faq-panel-${idx}`;
    const buttonId = `faq-trigger-${idx}`;
    return (
        <div
            className={`card-warm overflow-hidden transition-colors ${open ? "ring-1 ring-[var(--brand-purple)]/20" : ""}`}
            data-testid={`faq-item-${idx}`}
        >
            <button
                id={buttonId}
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                aria-controls={panelId}
                data-testid={`faq-trigger-${idx}`}
                className="w-full flex items-start sm:items-center justify-between gap-3 sm:gap-4 text-left p-4 sm:p-5 hover:bg-[rgba(123,45,142,0.03)] transition-colors"
            >
                <span className="font-serif text-base sm:text-lg md:text-xl leading-snug flex-1 min-w-0 break-words">
                    {question}
                </span>
                <span
                    className={`shrink-0 mt-1 sm:mt-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                        open ? "brand-gradient text-white rotate-180" : "bg-[rgba(26,19,24,0.05)] text-[var(--text-ink)]/60"
                    }`}
                    aria-hidden="true"
                >
                    <ChevronDown size={16} />
                </span>
            </button>
            <div
                id={panelId}
                role="region"
                aria-labelledby={buttonId}
                hidden={!open}
                data-testid={`faq-panel-${idx}`}
                className="px-4 sm:px-5 pb-5 pt-0 text-[var(--text-ink)]/80 text-sm sm:text-base leading-relaxed break-words"
            >
                {answer}
            </div>
        </div>
    );
}



function FinalCTA() {
    return (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 pb-24 relative z-[2]" data-testid="final-cta">
            <div
                className="p-10 md:p-14 text-white relative overflow-hidden rounded-[18px] shadow-lg"
                style={{ background: "linear-gradient(92deg, var(--brand-purple) 0%, var(--brand-magenta) 50%, var(--brand-orange) 100%)" }}
            >
                <div className="absolute -bottom-20 -right-20 w-72 h-72 rounded-full bg-white/10 pointer-events-none" />
                <div className="font-script text-2xl text-white/90 relative">ready when you are</div>
                <h2 className="font-serif text-4xl md:text-5xl mt-1 max-w-3xl relative text-white">
                    Run your work, your studies, and your life — without losing a thing.
                </h2>
                <p className="mt-3 text-white/90 max-w-2xl text-lg relative">
                    Seven days free. Cancel any time. No credit card. Just pick your mode and breathe out.
                </p>
                <div className="mt-6 flex flex-wrap gap-3 relative">
                    <Link to="/signup" data-testid="cta-signup" className="bg-white text-[var(--brand-purple)] font-medium rounded-full px-5 py-2.5 inline-flex items-center gap-2 hover:gap-3 transition-all">
                        Start free trial <ArrowRight size={16} />
                    </Link>
                    <Link to="/login" className="text-white/90 hover:text-white underline underline-offset-4 self-center">Already a member? Sign in</Link>
                </div>
            </div>
        </section>
    );
}

function FooterMini() {
    return (
        <footer className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 pb-10 flex flex-wrap items-center gap-3 justify-between text-sm text-[var(--text-ink)]/60" data-testid="landing-footer">
            <div>© {new Date().getFullYear()} Virtually Unstoppable.</div>
            <div className="flex gap-4">
                <Link to="/privacy" className="hover:text-[var(--brand-purple)]">Privacy</Link>
                <Link to="/terms" className="hover:text-[var(--brand-purple)]">Terms</Link>
            </div>
        </footer>
    );
}
